/**
 * Shareable work item URLs use `/issues/work-items/:workItemId?projectId=…`
 * so users with only global work-item permissions (e.g. My work items) are not
 * blocked by `/projects/:id` workspace guards.
 */

const VALID_RETURN_TABS = new Set(["dashboard", "issues", "boards", "backlog", "settings"]);

export function normalizeWorkItemDetailReturnTab(returnTab, fallback = "issues") {
  const t = returnTab != null ? String(returnTab).trim() : "";
  if (VALID_RETURN_TABS.has(t)) return t;
  return fallback;
}

/**
 * @param {{ projectSlug: string, workItemId: string }} params — projectSlug is the project UUID/key
 * @returns {string} e.g. `/issues/work-items/w1?projectId=p1`
 */
export function buildWorkItemDetailPath({ projectSlug, workItemId }) {
  const projectId = encodeURIComponent(String(projectSlug ?? "").trim());
  const id = encodeURIComponent(String(workItemId ?? "").trim());
  if (!projectId || !id) return "";
  const params = new URLSearchParams();
  params.set("projectId", projectId);
  return `/issues/work-items/${id}?${params.toString()}`;
}

export function buildWorkItemDetailShareUrl(params) {
  const path = buildWorkItemDetailPath(params);
  if (!path) return "";
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}

export async function copyWorkItemDetailLink(params) {
  const url = buildWorkItemDetailShareUrl(params);
  if (!url) throw new Error("Missing project or work item id");
  await navigator.clipboard.writeText(url);
  return url;
}

/**
 * Canonical share view: `/issues/work-items/:workItemId?projectId=…`
 * @param {{ pathname?: string, search?: string }} location
 */
export function isSharedWorkItemDetailLocation(location) {
  const pathname = String(location?.pathname ?? "");
  const match = pathname.match(/^\/issues\/work-items\/([^/]+)\/?$/i);
  if (!match) return false;
  const params = new URLSearchParams(String(location?.search ?? ""));
  return Boolean(params.get("projectId")?.trim());
}

/**
 * Build redirect target for legacy bookmark URLs, or null if not a legacy share link.
 * @param {{ pathname?: string, search?: string }} location
 * @param {{ projectSlug?: string, pathWorkItemId?: string }} routeParams
 */
export function resolveLegacyShareLinkRedirect(location, routeParams = {}) {
  const pathname = String(location?.pathname ?? "");
  const params = new URLSearchParams(String(location?.search ?? ""));

  const canonical = pathname.match(/^\/issues\/work-items\/([^/]+)\/?$/i);
  if (canonical) {
    const workItemId = decodeURIComponent(canonical[1]);
    const projectId = params.get("projectId")?.trim();
    if (projectId && workItemId) {
      const next = buildWorkItemDetailPath({ projectSlug: projectId, workItemId });
      if (next === `${pathname}${location?.search ?? ""}`) return null;
      return next;
    }
  }

  const legacyProjectQuery = pathname.match(/^\/projects\/([^/]+)\/?$/i);
  if (legacyProjectQuery) {
    const projectSlug = decodeURIComponent(legacyProjectQuery[1]);
    const workItemId = params.get("workItemId")?.trim();
    if (projectSlug && workItemId) {
      return buildWorkItemDetailPath({ projectSlug, workItemId });
    }
  }

  const legacyPath = parseLegacyWorkItemDetailPathname(pathname);
  if (legacyPath?.projectSlug && legacyPath?.workItemId) {
    return buildWorkItemDetailPath({
      projectSlug: legacyPath.projectSlug,
      workItemId: legacyPath.workItemId,
    });
  }

  const pathWorkItemId = routeParams.pathWorkItemId ?? routeParams.workItemId;
  const projectSlug = routeParams.projectSlug;
  if (projectSlug && pathWorkItemId) {
    return buildWorkItemDetailPath({
      projectSlug: String(projectSlug),
      workItemId: String(pathWorkItemId),
    });
  }

  return null;
}

/** Legacy share URL: `/projects/:slug/work-items/:id` (optional `?returnTab=`). */
export function parseLegacyWorkItemDetailPathname(pathname) {
  const match = String(pathname ?? "").match(/^\/projects\/([^/]+)\/work-items\/([^/]+)\/?$/i);
  if (!match) return null;
  try {
    return {
      projectSlug: decodeURIComponent(match[1]),
      workItemId: decodeURIComponent(match[2]),
    };
  } catch {
    return { projectSlug: match[1], workItemId: match[2] };
  }
}
